/**
 * @fileoverview Frame explicit confirmation of selected document/folder trash actions. The parent supplies targets and owns execution, pending state, and accepted results. Confirmation here expresses user intent; backend governance and access checks still determine whether each target can be trashed.
 */

import React, { useEffect, useRef } from 'react';
import './DocumentTrashDialog.css';



function containModalFocus(event) {
  if (event.key !== 'Tab') return;
  const focusable = [...event.currentTarget.querySelectorAll(
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )].filter((element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true');
  if (!focusable.length) {
    event.preventDefault();
    event.currentTarget.focus();
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}



function DocumentTrashDialog({ request, busy, error = '', onCancel, onConfirm }) {
  const cancelRef = useRef(null);
  useEffect(() => {
    if (request) cancelRef.current?.focus();
  }, [request]);
  if (!request) return null;
  const count = Number(request.count || 0);
  return (
    <div className="document-trash-dialog-backdrop" role="presentation">
      <section
        className="document-trash-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="document-trash-dialog-title"
        aria-describedby="document-trash-dialog-description"
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !busy) onCancel();
          containModalFocus(event);
        }}>
        <header>
          <h2 id="document-trash-dialog-title">Move {request.folderName ? `“${request.folderName}”` : `${count} ${count === 1 ? 'document' : 'documents'}`} to trash?</h2>
        </header>
        <p id="document-trash-dialog-description">{request.folderName ? 'This folder and everything inside it will move to Trash. You can restore eligible items from Trash.' : 'The selection will leave its current folders. You can restore eligible items from Trash.'}</p>
        {error && <div className="document-trash-dialog-error" role="alert">{error}</div>}
        <footer>
          <button ref={cancelRef} type="button" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" className="is-danger" onClick={onConfirm} disabled={busy}>{busy ? 'Moving…' : 'Move to trash'}</button>
        </footer>
      </section>
    </div>
  );
}

export default DocumentTrashDialog;
